import clsx from 'clsx';
import { forwardRef } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { strikethroughCSS } from './Strikethrough.sty.js';

const COMPONENT_NAME = 'Strikethrough';

/**
 * The props for the Strikethrough component.
 * @public
 */
export interface StrikethroughProps
  extends
    WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {}

/**
 * The `Strikethrough` component renders text with a line through it. Use it to
 * represent things that are no longer relevant or accurate.
 * @public
 */
export const Strikethrough = /* @__PURE__ */ forwardRef<
  HTMLElement,
  StrikethroughProps
>((props, ref) => {
  const {
    children,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  return (
    <s
      ref={ref}
      className={clsx(strikethroughCSS, consumerClassName)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </s>
  );
});
(Strikethrough as typeof Strikethrough & { displayName: string }).displayName =
  COMPONENT_NAME;
